'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';

import {
  CAMERA_VIDEO_CONSTRAINTS,
  cameraPrefKey,
} from './project-usability-camera.consts';
import { mapMediaError } from './project-usability-camera.helpers';
import { stateManager } from './project-usability-recording-state-manager';
import { getServerRecordingSnapshot } from './project-usability-recording-state.helpers';

interface CameraPref {
  enabled: boolean;
  deviceId: string | null;
}

function isCameraPref(value: unknown): value is CameraPref {
  if (typeof value !== 'object' || value === null) return false;
  if (!('enabled' in value) || typeof value.enabled !== 'boolean') return false;
  if (!('deviceId' in value)) return false;
  return value.deviceId === null || typeof value.deviceId === 'string';
}

function readPref(projectId: string): CameraPref | null {
  try {
    const raw = window.sessionStorage.getItem(cameraPrefKey(projectId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isCameraPref(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writePref(projectId: string, pref: CameraPref): void {
  try {
    window.sessionStorage.setItem(
      cameraPrefKey(projectId),
      JSON.stringify(pref)
    );
  } catch {
    return;
  }
}

export function useUsabilityCamera(projectId: string) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  const {
    isCameraActive,
    isCameraOn,
    cameraStream,
    cameraDeviceId,
    cameraError,
    isRecording,
  } = useSyncExternalStore(
    stateManager.subscribe,
    stateManager.getSnapshot,
    getServerRecordingSnapshot
  );

  async function refreshDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter((device) => device.kind === 'videoinput');
      setDevices(cams);
      return cams;
    } catch {
      setDevices([]);
      return [];
    }
  }

  function pauseCamera(): void {
    stateManager.stopCameraTracks();
    writePref(projectId, {
      enabled: false,
      deviceId: stateManager.getSnapshot().cameraDeviceId,
    });
  }

  async function acquire(deviceId?: string): Promise<void> {
    const generation = stateManager.beginCameraAcquire();
    const target =
      deviceId ?? stateManager.getSnapshot().cameraDeviceId ?? undefined;
    const cams = await refreshDevices();

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: target
          ? { deviceId: { exact: target }, ...CAMERA_VIDEO_CONSTRAINTS }
          : { ...CAMERA_VIDEO_CONSTRAINTS },
      });
    } catch (err) {
      if (stateManager.getCameraGeneration() !== generation) return;
      stateManager.setCameraError(mapMediaError(err, cams.length));
      if (!stateManager.getCameraStream()) {
        stateManager.setCameraOn(false);
      }
      return;
    }

    if (stateManager.getCameraGeneration() !== generation) {
      stream.getTracks().forEach((orphan) => orphan.stop());
      return;
    }
    await refreshDevices();
    if (stateManager.getCameraGeneration() !== generation) {
      stream.getTracks().forEach((orphan) => orphan.stop());
      return;
    }

    const [track] = stream.getVideoTracks();
    track?.addEventListener(
      'ended',
      () => {
        if (
          stateManager.getCameraStream() === stream &&
          stateManager.getCameraGeneration() === generation
        ) {
          pauseCamera();
        }
      },
      { once: true }
    );

    const previous = stateManager.getCameraStream();
    stateManager.setCameraStream(stream);
    stateManager.setCameraDeviceId(
      track?.getSettings?.().deviceId ?? target ?? null
    );
    stateManager.setCameraError(null);
    if (previous && previous !== stream) {
      previous.getTracks().forEach((oldTrack) => oldTrack.stop());
    }
    stateManager.setCameraActive(true);
    stateManager.setCameraOn(true);
    writePref(projectId, {
      enabled: true,
      deviceId: stateManager.getSnapshot().cameraDeviceId,
    });
  }

  function enableCamera(deviceId?: string): Promise<void> {
    return acquire(deviceId);
  }

  function switchCamera(deviceId: string): Promise<void> {
    return acquire(deviceId);
  }

  function deactivateCamera(): void {
    stateManager.stopCameraTracks();
    stateManager.setCameraActive(false);
    stateManager.setCameraError(null);
    writePref(projectId, {
      enabled: false,
      deviceId: stateManager.getSnapshot().cameraDeviceId,
    });
  }

  async function restorePreference(): Promise<void> {
    const pref = readPref(projectId);
    if (pref?.deviceId) stateManager.setCameraDeviceId(pref.deviceId);
    if (pref?.enabled) await acquire(pref.deviceId ?? undefined);
  }

  return {
    isCameraActive,
    isCameraOn,
    cameraDeviceId,
    cameraError,
    isRecording,
    cameraStream,
    devices,
    enableCamera,
    pauseCamera,
    deactivateCamera,
    switchCamera,
    restorePreference,
  };
}

export function useResetCameraOnUnmount(projectId: string): void {
  useEffect(() => {
    return () => {
      stateManager.stopCameraTracks();
      stateManager.setCameraActive(false);
      stateManager.setCameraError(null);
      try {
        window.sessionStorage.removeItem(cameraPrefKey(projectId));
      } catch {
        return;
      }
    };
  }, [projectId]);
}
